import format from 'date-fns/format';
import parse from 'date-fns/parse';
import { Invoice, InvoiceStatement } from '../../..';
import { i18n } from '../../../i18n-handler/handler/i18n';
import { PricesUtilityHandler } from '../../handler/prices-utility-handler';

let lang = 'fr';

/**
 * PERIOD_IN_PROGRESS => période en cours
 * PAID => payée
 * CANCELLED => annulée
 * TO_PAY => à payer
 * PAYIN_IN_PROGRESS => le payin est en attente de validation par la banque
 * SEPA_PAYIN_IN_DISPUTE => le payin est en litige
 */
function getInvoiceStatusAndClass(invoiceStatement: InvoiceStatement | Invoice): { status: string; class: string } {
    let _status = '';
    let _class = '';

    if (invoiceStatement.status === 'PERIOD_IN_PROGRESS') {
        _status = `•\u00A0${i18n.t('invoice.status.period_in_progress', { lng: lang })}`;
        _class = 'in_progress';
    } else if (invoiceStatement.status === 'PAID') {
        _status = `•\u00A0${i18n.t('invoice.status.paid', { lng: lang })}`;
        _class = 'success';
    } else if (invoiceStatement.status === 'CANCELLED') {
        _status = `•\u00A0${i18n.t('invoice.status.cancelled', { lng: lang })}`;
        _class = 'cancelled';
    } else if (invoiceStatement.status === 'TO_PAY') {
        _status = `•\u00A0${i18n.t('invoice.status.to_pay', { lng: lang })}`;
        _class = 'waiting';
    } else if (invoiceStatement.status === 'PAYIN_IN_PROGRESS') {
        _status = `•\u00A0${i18n.t('invoice.status.payin_in_progress', { lng: lang })}`;
        _class = 'waiting';
    } else if (invoiceStatement.status === 'SEPA_PAYIN_IN_DISPUTE') {
        _status = `•\u00A0${i18n.t('invoice.status.in_dispute', { lng: lang })}`;
        _class = 'in-dispute';
    } else {
        _status = '-';
    }

    return { status: _status, class: _class };
}

function getInvoiceStatementPeriodText(invoiceStatement: InvoiceStatement): string {
    let periodText = '';
    if (invoiceStatement && invoiceStatement.start_date && invoiceStatement.end_date) {
        periodText = `${format(new Date(invoiceStatement.start_date), 'dd/MM')} - ${format(
            new Date(invoiceStatement.end_date),
            'dd/MM/yyyy'
        )}`;
    }
    return periodText;
}

function getInvoiceDatesText(invoice: Invoice): string {
    let datesText = '';
    if (invoice && invoice.dates) {
        if (invoice.dates && invoice.dates.length === 1) {
            const parsed = parse(invoice.dates[0], 'yyyy-MM-dd', new Date());
            datesText = `${format(parsed, 'dd/MM/yyyy')}`;
        } else {
            const firstDate = parse(invoice.dates[0], 'yyyy-MM-dd', new Date());
            const lastDate = parse(invoice.dates[invoice.dates.length - 1], 'yyyy-MM-dd', new Date());
            datesText = `${format(firstDate, 'dd/MM')} - ${format(lastDate, 'dd/MM/yyyy')}`;
        }
    }
    return datesText;
}

function getInvoiceNbDaysText(invoice: Invoice): string {
    let nbDaysText = '';
    if (invoice && invoice.dates && invoice.dates.length > 1) {
        nbDaysText = `(${invoice.dates.length} ${i18n.t('invoice.details.days', { lng: lang })})`;
    }
    return nbDaysText;
}

function getInvoiceTimesText(invoice: Invoice): string {
    let timesText = '';
    if (invoice && invoice.times) {
        if (invoice.times.has_break) {
            timesText = `${invoice.times.start_time} - ${invoice.times.end_time}\n`;
            timesText += `${invoice.times.break_start_time} - ${invoice.times.break_end_time}`;
        } else {
            timesText = `${invoice.times.start_time} - ${invoice.times.end_time}`;
        }
    }
    return timesText;
}

/**
 * @example 123€ HT
 * @example 123€ HT (140€ TTC) (using showWithTaxes=true)
 */
function getInvoiceAmountsText(invoice: Invoice, showWithTaxes = true): string {
    let amountsText = '';

    if (invoice?.amounts?.withoutTaxes) {
        amountsText = PricesUtilityHandler.formatAmountsTextWithTaxLabel(invoice.amounts.withoutTaxes);
    }

    if (invoice.amounts?.withoutTaxes && invoice.amounts?.withTaxes && showWithTaxes) {
        amountsText = PricesUtilityHandler.formatAmountsTextDetailWithTaxLabel(
            invoice.amounts.withoutTaxes,
            invoice.amounts.withTaxes
        );
    }

    return amountsText;
}

export type ProFormatReturn = {
    getInvoiceStatusAndClass: Function;
    getInvoiceStatementPeriodText: Function;
    getInvoiceDatesText: Function;
    getInvoiceNbDaysText: Function;
    getInvoiceTimesText: Function;
    getInvoiceAmountsText: Function;
};

export const proFormat = (_lang = 'fr'): ProFormatReturn => {
    lang = _lang;
    return {
        getInvoiceStatusAndClass,
        getInvoiceStatementPeriodText,
        getInvoiceDatesText,
        getInvoiceNbDaysText,
        getInvoiceTimesText,
        getInvoiceAmountsText,
    };
};
